import React from "react";
import tw from "twin.macro";
import styled from "styled-components";

const Section = styled.section`
  ${tw`relative -mx-8 font-work_sans`}
  background: #fffbdc;
  padding: clamp(1.5rem, 1.2rem + 1.53vw, 3rem) clamp(1.5rem, 1rem + 2vw, 3.5rem);
`;

const Inner = tw.div`max-w-screen-xl mx-auto`;

const HeadingBlock = styled.div`
  ${tw`text-center flex flex-col items-center gap-4`}
  max-width: 50rem;
  margin: 0 auto clamp(3rem, 2.4rem + 2vw, 4.5rem);
`;

const Eyebrow = tw.span`uppercase tracking-[0.28em] text-[#14133B]/75 text-xs sm:text-sm`;

const Heading = tw.h2`text-3xl sm:text-4xl md:text-5xl font-semibold text-dark-500`;

const CardsGrid = styled.div`
  ${tw`grid gap-8`}
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
`;

const StoreTile = styled.div`
  ${tw`flex flex-col gap-4`}
`;

const StoreCard = styled.div`
  ${tw`relative rounded-3xl w-full overflow-hidden`}
  background-image: ${({ $image }) => `url("${$image}")`};
  background-size: cover;
  background-position: center;
  border: ${({ $comingSoon }) => ($comingSoon ? "1px solid #14133b" : "1px solid transparent")};
  box-shadow: ${({ $comingSoon }) => ($comingSoon ? "none" : "0 18px 36px rgba(20, 19, 65, 0.22)")};
  filter: ${({ $comingSoon }) => ($comingSoon ? "blur(6px)" : "none")};

  &::before {
    content: "";
    display: block;
    padding-top: 100%;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
  }
`;

const StoreInfo = styled.div`
  ${tw`flex flex-col gap-4`}
  text-align: left;
  letter-spacing: 0.05em;
`;

const StoreTitle = styled.div`
  font-size: 3rem;
  text-align: left;
  font-weight: 700;
  color: #FAB600;
  letter-spacing: 0.01em;
  font-family: "Work Sans", sans-serif;
`;

const MetaGroup = styled.div`
  ${tw`flex flex-col gap-2`}
`;

const ComingSoonTag = styled.span`
  ${tw`inline-flex items-center justify-center uppercase font-semibold`}
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  padding: clamp(0.5rem, 0.4rem + 0.4vw, 0.8rem) clamp(1.4rem, 1.2rem + 0.6vw, 1.9rem);
  border-radius: 999px;
  color: #14133b;
  letter-spacing: 0.05em;
  font-size: 2.3rem;
  text-align: center;
`;

const StoreMeta = styled.div`
  ${tw`flex flex-col gap-4 text-sm sm:text-base`}
  font-family: "Work Sans", sans-serif;
  line-height: 1.6;
  color: #14133b;
`;

const MetaLabel = tw.span`uppercase tracking-[0.22em] text-xs text-[#14133B]/75`;

const MetaContent = tw.span`font-medium text-[#14133B]`;

const HoursList = styled.ul`
  ${tw`flex flex-col gap-1 mt-1`}
  list-style: none;
  padding: 0;
  margin: 0;
`;

const HoursLine = styled.li`
  ${tw`text-sm sm:text-base`}
  color: #14133b;
`;

const StoreActions = styled.div`
  ${tw`flex flex-wrap gap-3 mt-3`}
`;

const StoreButton = styled.a`
  ${tw`inline-flex items-center justify-center font-semibold uppercase`}
  border-radius: 999px;
  padding: 0.8rem 1.8rem;
  letter-spacing: 0.05em;
  background: #14133b;
  color: #fffbdc;
  text-decoration: none;
  transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 18px 32px rgba(20, 19, 65, 0.18);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const StoreComingSoon = styled.span`
  ${tw`inline-flex items-center uppercase font-semibold tracking-[0.18em]`}
  color: #14133b;
  letter-spacing: 0.05em;
  margin-top: 0.75rem;
`;

const STORES = [
  {
    name: "Holborn",
    image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/foundation/9081762980371_.pic_hd.jpg",
    address: "272 High Holborn, London WC1V 7EY",
    hours: [
      {
        label: "Opening Time",
        lines: ["Monday to Thursday 08:00 - 20:00", "Friday 08:00 – 16:00", "Saturday 11:30 – 16:00", "Sunday Closed", "Bank Holiday Closed"],
      }
    ],
    map: "https://www.google.com/maps?q=272+High+Holborn+London+WC1V+7EY",
  },
  {
    name: " ",
    image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/foundation/9081762980371_.pic_hd.jpg",
    comingSoon: true,
  },
  {
    name: "   ",
    image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/foundation/9081762980371_.pic_hd.jpg",
    comingSoon: true,
  },
];

const StoresComponent = () => {
  return (
    <Section>
      <Inner>
        <HeadingBlock>
          <Eyebrow>Visit us in London</Eyebrow>
          <Heading>Our Stores</Heading>
        </HeadingBlock>

        <CardsGrid>
          {STORES.map((store) => (
            <StoreTile key={store.name}>
              <StoreCard $image={store.image} $comingSoon={store.comingSoon} aria-hidden="true">
                {store.comingSoon && <ComingSoonTag> </ComingSoonTag>}
              </StoreCard>

              <StoreInfo>
                <StoreTitle>{store.name}</StoreTitle>

                {!store.comingSoon && (
                  <StoreMeta>
                    <MetaGroup>
                      <MetaLabel>Address</MetaLabel>
                      <MetaContent>{store.address}</MetaContent>
                    </MetaGroup>
                    {store.hours?.map((block) => (
                      <MetaGroup key={block.label}>
                        <MetaLabel>{block.label}</MetaLabel>
                        <HoursList>
                          {block.lines.map((line) => (
                            <HoursLine key={line}>{line}</HoursLine>
                          ))}
                        </HoursList>
                      </MetaGroup>
                    ))}
                  </StoreMeta>
                )}
              </StoreInfo>

              {store.comingSoon ? (
                <StoreComingSoon>COMING SOON</StoreComingSoon>
              ) : (
                <StoreActions>
                  <StoreButton href={store.map} target="_blank" rel="noopener noreferrer">
                    View Directions
                  </StoreButton>
                </StoreActions>
              )}
            </StoreTile>
          ))}
        </CardsGrid>
      </Inner>
    </Section>
  );
};

export default StoresComponent;
